import { type CustomDirectusTypes } from '@funnellab/shared/src/types/directus.ts';
import { type JobStatus } from '@funnellab/shared/src/types/directus-enums.ts';
import { JobStatusBadge } from '@components/JobStatusBadge.tsx';
import { NO_PAGINATION_PAGE_SIZE } from '@src/config/constants';
import { RowActions } from '@src/components/jobOverview/RowActions';
import { directusClient } from '@src/api/DirectusClient.ts';
import { getJobDetailsUrl } from '@src/config/paths';
import { organizationJobsQuery } from '@src/api/queries/oragnizationJobs.ts';
import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DirectusTable, {
  type DirectusTableHandle,
} from '@components/directusTable/DirectusTable.tsx';

export default function Jobs({ organizationId }: { organizationId: string }) {
  const tableRef = useRef<DirectusTableHandle>(undefined);

  const navigate = useNavigate();
  const [t] = useTranslation('view', { keyPrefix: 'jobs' });

  const query = useMemo(
    () => ({
      ...organizationJobsQuery,
      filter: { organization: organizationId },
    }),
    [organizationId],
  );

  const reload = useCallback(() => tableRef.current?.reload(true), []);

  return (
    <DirectusTable<CustomDirectusTypes, 'job', typeof query>
      ref={tableRef}
      itemsPerPage={NO_PAGINATION_PAGE_SIZE}
      collection="job"
      directusClient={directusClient._client}
      query={query}
      embedded
      selectedColumns={['idExternal', 'title', 'status', 'actions']}
      getId={(rowData) => rowData.id}
      columnOverrides={{
        idExternal: {
          sortable: true,
          headContent: t('columnId'),
          renderCell: ({ rowData }) => <div>{rowData.idExternal ?? '-'}</div>,
          cellClassName: '!hidden sm:!table-cell',
        },
        title: {
          sortable: true,
          headContent: t('columnTitle'),
          renderCell: ({ rowData }) => <div>{rowData.title ?? ''}</div>,
          cellClassName:
            '!font-medium sm:!font-normal text-ellipsis !pr-25 sm:!pr-3',
        },
        status: {
          sortable: true,
          renderCell: ({ rowData }) => (
            <JobStatusBadge
              state={rowData.status as JobStatus}
              nextRenewal={rowData.nextRenewal}
            />
          ),
          headContent: t('columnStatus'),
          cellClassName: '!absolute right-3 top-6 sm:!static sm:!table-cell',
        },
        actions: {
          headContent: '',
          sortable: false,
          renderCell: ({ rowData }) => (
            <RowActions rowData={rowData} reload={reload} />
          ),
          cellClassName: '!hidden sm:!table-cell sm:min-w-28',
        },
      }}
      onRowClick={(rowData, event) => {
        if (event.defaultPrevented) {
          // This happens when a button in a cell is clicked. In that case, the row should not be clicked.
          return;
        }

        navigate(getJobDetailsUrl(rowData.id));
      }}
      getRowLink={getJobDetailsUrl}
    />
  );
}
